<template>
    <div class="container-nzidoo">
        <page-title title="chatbot.title" />

        <!-- Toolbar -->
        <div class="admin-toolbar">
            <div class="admin-search-box">
                <i class="mdi mdi-magnify admin-search-icon"></i>

                <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')"
                    class="admin-search-input">

                <!-- Clear icon -->
                <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
                    :title="$t('sentence.clear')"></i>
            </div>

            <button class="btn btn-primary" @click="openCreateModal">
                <i class="mdi mdi-plus"></i>
                <div class="d-none d-md-block">
                    {{ $t("action.add") }}
                </div>
            </button>
        </div>

        <LoadingSpinner v-if="loader" />
        <!-- Messages Grid -->
        <div class="admin-grid">
            <div v-for="msg in filteredMessages" :key="msg.id" class="admin-card">
                <div class="admin-card-header">
                    <span class="chatbot-card-id">{{ $t('chatbot.id') }}: {{ msg.id }}</span>
                    <div class="admin-card-actions">
                        <button class="btn btn-icon-action btn-warning" @click="edit(msg.id)"
                            :title="$t('action.edit')">
                            <i class="mdi mdi-pencil"></i>
                        </button>
                        <button class="btn btn-icon-action btn-danger" @click="remove(msg.id)"
                            :title="$t('action.delete')">
                            <i class="mdi mdi-delete"></i>
                        </button>
                    </div>
                </div>
                <div class="admin-card-body">
                    <div class="admin-card-section">
                        <label class="admin-card-label">{{ $t('chatbot.keywords') }}</label>
                        <div class="card-name">{{ msg.keywords }}</div>
                    </div>
                    <div class="admin-card-section">
                        <label class="admin-card-label">{{ $t('chatbot.answer') }}</label>
                        <div class="card-light">{{ msg.answer }}</div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="filteredMessages.length === 0" class="admin-empty-state">
                <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
            </div>
        </div>

        <!-- Drawer Backdrop -->
        <transition name="drawer-backdrop">
            <div v-if="showModal" class="admin-drawer-backdrop" @click="closeModal"></div>
        </transition>

        <!-- Drawer -->
        <transition name="drawer">
            <div v-if="showModal" class="admin-drawer-container chatbot-drawer-narrow">
                <div class="admin-drawer-header">
                    <h3 class="admin-drawer-title">
                        {{ isEditing ? $t('chatbot.editMessage') : $t('chatbot.addMessage') }}
                    </h3>
                    <button class="btn btn-close" @click="closeModal">
                        <i class="mdi mdi-close"></i>
                    </button>
                </div>

                <div class="admin-drawer-body">
                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('chatbot.keywords') }}</label>
                        <input type="text" v-model="form.keywords" class="admin-form-input"
                            :placeholder="$t('chatbot.keywordsPlaceholder')">
                        <span class="admin-form-hint">{{ $t('chatbot.keywordsHint') }}</span>
                    </div>

                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('chatbot.answer') }}</label>
                        <textarea v-model="form.answer" class="admin-form-textarea" rows="5"
                            :placeholder="$t('chatbot.answerPlaceholder')"></textarea>
                    </div>
                </div>

                <div class="admin-drawer-footer">
                    <button class="btn btn-secondary" @click="closeModal">
                        {{ $t('action.cancel') }}
                    </button>
                    <button class="btn btn-primary" @click="save">
                        {{ isEditing ? $t('action.update') : $t('action.create') }}
                    </button>
                </div>
            </div>
        </transition>

        <notifications style="top:5%;" width="35%" group="error" position="top center" />
        <notifications style="top:5%;" width="35%" group="success" position="top center" />
    </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import AlertService from '@/services/alertService';

export default {
    components: { PageTitle, LoadingSpinner },
    name: "ChatbotAdmin",

    data() {
        return {
            messages: [],
            showModal: false,
            isEditing: false,
            editId: null,
            searchTerm: "",
            loader: false,
            form: {
                keywords: "",
                answer: ""
            }
        };
    },

    computed: {
        filteredMessages() {
            if (!this.searchTerm) return this.messages;

            const term=this.searchTerm.toLowerCase();
            return this.messages.filter(msg =>
                msg.keywords.toLowerCase().includes(term)||
                msg.answer.toLowerCase().includes(term)
            );
        }
    },

    mounted() {
        this.loadMessages();
    },

    methods: {
        clearSearch() {
            this.searchTerm="";
        },
        async loadMessages() {
            try {
                this.loader=true;
                const res=await this.$axios.get("/admin/chatbot");
                this.messages=Array.isArray(res.data.data)
                    ? res.data.data
                    :Object.values(res.data.data);
                this.loader=false;
            } catch (err) {
                console.error("Error loading messages:", err);
                AlertService.error(this.$t('sentence.errorLoading')||"Erreur lors du chargement des messages");
            }
        },

        openCreateModal() {
            this.isEditing=false;
            this.showModal=true;
            this.form={ keywords: "", answer: "" };
        },

        closeModal() {
            this.showModal=false;
        },

        async save() {
            try {
                if (this.isEditing) {
                    const res=await this.$axios.put(`/admin/chatbot/${this.editId}`, this.form);
                    // AlertService.success(this.$t('sentence.updateSuccess')||"Message mis à jour avec succès");
                    this.$notify({
                        group: "success",
                        title: res.data.message,
                        type: "success",
                    });
                } else {
                    const res=await this.$axios.post("/admin/chatbot", this.form);
                    // AlertService.success(this.$t('sentence.createSuccess')||"Message créé avec succès");
                    this.$notify({
                        group: "success",
                        title: res.data.message,
                        type: "success",
                    });
                }
                this.closeModal();
                this.loadMessages();
            } catch (err) {
                console.error("Error saving message:", err);
                this.$notify({
                    group: "error",
                    title: err||"Error saving message",
                    type: "error",
                });
                // AlertService.error(this.$t('sentence.errorSaving')||"Erreur lors de l'enregistrement");
            }
        },

        async edit(id) {
            this.isEditing=true;
            this.editId=id;

            try {
                const res=await this.$axios.get(`/admin/chatbot/${id}`);
                this.form={ ...res.data };
                this.showModal=true;
                this.$notify({
                    group: "success",
                    title: res.data.message,
                    type: "success",
                });
            } catch (err) {
                console.error("Error loading message:", err);
                this.$notify({
                    group: "error",
                    title: err||"Error loading message",
                    type: "error",
                });
                // AlertService.error(this.$t('chatbot.errorLoading')||"Erreur lors du chargement du message");
            }
        },

        async remove(id) {
            const confirmed=await AlertService.confirm({
                title: this.$t('sentence.confirmDelete')||"Êtes-vous sûr ?",
                confirmButtonText: this.$t('chatbot.delete')||"Supprimer",
                cancelButtonText: this.$t('chatbot.cancel')||"Annuler",
                icon: "warning"
            });

            if (!confirmed) return;

            try {
                const res=await this.$axios.delete(`/admin/chatbot/${id}`);
                this.$notify({
                    group: "success",
                    title: res.data.message,
                    type: "success",
                });
                // AlertService.success(this.$t('sentence.deleteSuccess')||"Message supprimé avec succès");
                this.loadMessages();
            } catch (err) {
                console.error("Error deleting message:", err);
                this.$notify({
                    group: "error",
                    title: err||"Error deleting message",
                    type: "error",
                });
                // AlertService.error(this.$t('sentence.errorDeleting')||"Erreur lors de la suppression");
            }
        }
    }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Component-specific styles */
.chatbot-card-id {
    font-size: 13px;
    font-weight: 600;
    color: var(--gray-600);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Narrower drawer for chatbot */
.chatbot-drawer-narrow {
    max-width: 500px;
}

@media (max-width: 768px) {
    .chatbot-drawer-narrow {
        max-width: 100%;
    }
}
</style>